import { Injectable } from '@angular/core';

/**
 * Client-side persistence for the per-attribute `readOnly` flag.
 *
 * <p>The backend (`tasksense-userdata-service`) does NOT yet have a
 * `read_only` column on `user_data_type_attribute`, so any value sent via
 * the attribute-create / update requests is silently dropped. Until that
 * lands, this store keeps the flag in {@code localStorage} keyed by
 * "{type}::{name}" so the UserData edit screen can render fields as
 * read-only across reloads.
 *
 * <p>**Caveat:** browser-local only — different machines / users won't
 * see each other's read-only marks. Replace with the backend-persisted
 * value once the column ships.
 */
@Injectable({ providedIn: 'root' })
export class UserDataTypeReadOnlyStore {

    private readonly STORAGE_KEY = 'ts.userdata.attr.readonly';

    isReadOnly(type: string, name: string): boolean {
        if (!type || !name) return false;
        return this.load()[this.compositeKey(type, name)] === true;
    }

    set(type: string, name: string, readOnly: boolean): void {
        if (!type || !name) return;
        const data = this.load();
        const key = this.compositeKey(type, name);
        if (readOnly) data[key] = true;
        else delete data[key];
        this.save(data);
    }

    private compositeKey(type: string, name: string): string {
        return `${type}::${name}`;
    }

    private load(): Record<string, boolean> {
        try {
            const raw = localStorage.getItem(this.STORAGE_KEY);
            return raw ? JSON.parse(raw) : {};
        } catch {
            return {};
        }
    }

    private save(data: Record<string, boolean>): void {
        try { localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data)); }
        catch { /* localStorage unavailable — fine */ }
    }
}
